/**
 * File Toolbar Component
 *
 * Toolbar with file operations: new, open, save, import, export.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useFileStore } from '../index';

export function FileToolbar() {
  const {
    currentFileName,
    hasUnsavedChanges,
    newDocument,
    saveModel,
    setImportDialogOpen,
    setExportDialogOpen,
    setSavedModelsDialogOpen,
    setExampleModelsDialogOpen,
    importFile,
    isSaving,
    autoSave,
    notification,
    clearNotification,
    setCurrentFileName,
  } = useFileStore();

  const [menuOpen, setMenuOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isEditingFilename, setIsEditingFilename] = useState(false);
  const [editedFilename, setEditedFilename] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filenameInputRef = useRef<HTMLInputElement>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [menuOpen]);

  const handleNew = useCallback(() => {
    if (hasUnsavedChanges) {
      if (confirm('You have unsaved changes. Create a new document anyway?')) {
        newDocument();
      }
    } else {
      newDocument();
    }
    setMenuOpen(false);
  }, [hasUnsavedChanges, newDocument]);

  const handleImport = useCallback(() => {
    setImportDialogOpen(true);
    setMenuOpen(false);
  }, [setImportDialogOpen]);

  const handleQuickImport = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && files[0]) {
      try {
        setSaveError(null);
        await importFile(files[0]);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Failed to import file');
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = setTimeout(() => { setSaveError(null); }, 5000);
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [importFile]);

  const handleSave = useCallback(async () => {
    try {
      setSaveError(null);
      await saveModel();
      setMenuOpen(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
      // Auto-clear error after 5 seconds
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => { setSaveError(null); }, 5000);
    }
  }, [saveModel]);

  const handleExport = useCallback(() => {
    setExportDialogOpen(true);
    setMenuOpen(false);
  }, [setExportDialogOpen]);

  const handleOpenSaved = useCallback(() => {
    setSavedModelsDialogOpen(true);
    setMenuOpen(false);
  }, [setSavedModelsDialogOpen]);

  const handleOpenExamples = useCallback(() => {
    setExampleModelsDialogOpen(true);
    setMenuOpen(false);
  }, [setExampleModelsDialogOpen]);

  const handleStartEditFilename = useCallback(() => {
    setEditedFilename(currentFileName ?? 'Untitled');
    setIsEditingFilename(true);
    // Focus the input after state updates
    setTimeout(() => {
      filenameInputRef.current?.focus();
      filenameInputRef.current?.select();
    }, 0);
  }, [currentFileName]);

  const handleSaveFilename = useCallback(() => {
    const trimmedName = editedFilename.trim();
    if (trimmedName && trimmedName !== currentFileName) {
      setCurrentFileName(trimmedName);
    }
    setIsEditingFilename(false);
  }, [editedFilename, currentFileName, setCurrentFileName]);

  const handleCancelEditFilename = useCallback(() => {
    setIsEditingFilename(false);
    setEditedFilename('');
  }, []);

  const handleFilenameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveFilename();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelEditFilename();
      }
    },
    [handleSaveFilename, handleCancelEditFilename]
  );

  return (
    <div className="flex items-center gap-2">
      {/* File menu button */}
      <div className="relative" ref={menuRef}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setMenuOpen(!menuOpen); }}
          className="gap-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          File
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Button>

        {menuOpen && (
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-md border bg-popover p-1 shadow-md">
            <MenuItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
              }
              label="New"
              shortcut="Ctrl+N"
              onClick={handleNew}
            />
            <div className="my-1 h-px bg-border" />
            <MenuItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              }
              label="Import IDF..."
              shortcut="Ctrl+O"
              onClick={handleImport}
            />
            <MenuItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              }
              label="Open Saved..."
              onClick={handleOpenSaved}
            />
            <MenuItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              }
              label="Example Models..."
              onClick={handleOpenExamples}
            />
            <div className="my-1 h-px bg-border" />
            <MenuItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
              }
              label="Save"
              shortcut="Ctrl+S"
              onClick={handleSave}
            />
            <MenuItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              }
              label="Export IDF..."
              shortcut="Ctrl+E"
              onClick={handleExport}
            />
          </div>
        )}
      </div>

      {/* Quick save button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleSave}
        disabled={isSaving}
        title="Save (Ctrl+S)"
      >
        {isSaving ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
        )}
      </Button>

      {/* File name display - responsive for all screen sizes */}
      <div className="flex items-center gap-1 text-sm">
        {isEditingFilename ? (
          <div className="relative flex items-center">
            <input
              ref={filenameInputRef}
              type="text"
              value={editedFilename}
              onChange={(e) => setEditedFilename(e.target.value)}
              onKeyDown={handleFilenameKeyDown}
              onBlur={handleCancelEditFilename}
              className="h-7 w-28 sm:w-40 rounded-md border bg-background px-2 pr-7 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              aria-label="Edit filename"
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSaveFilename}
              className="absolute right-1 flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              title="Save filename"
              aria-label="Save filename"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleStartEditFilename}
            className="max-w-[100px] sm:max-w-none cursor-pointer truncate rounded px-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            title="Click to edit filename"
            aria-label="Edit filename"
          >
            {currentFileName ?? 'Untitled'}
          </button>
        )}
        {hasUnsavedChanges && (
          <span className="text-yellow-600" title="Unsaved changes">
            *
          </span>
        )}
        {autoSave.enabled && autoSave.lastSaved && (
          <span className="hidden sm:inline text-xs text-muted-foreground" title="Auto-save enabled">
            (auto-saved)
          </span>
        )}
      </div>

      {/* Model load/create notification */}
      {notification && (
        <div
          className={`flex items-center gap-2 rounded-md border px-3 py-1 text-sm animate-in fade-in slide-in-from-left-2 duration-200 ${
            notification.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400'
              : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-400'
          }`}
        >
          {notification.type === 'success' ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          <span>{notification.message}</span>
          <button
            onClick={clearNotification}
            className="ml-1 opacity-60 hover:opacity-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Error message */}
      {saveError && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
          <span>{saveError}</span>
          <button
            onClick={() => { setSaveError(null); }}
            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Hidden file input for quick import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".idf,.imf"
        className="hidden"
        onChange={handleQuickImport}
      />
    </div>
  );
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
}

function MenuItem({ icon, label, shortcut, onClick, disabled }: MenuItemProps) {
  return (
    <button
      className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      {shortcut && (
        <span className="text-xs text-muted-foreground">{shortcut}</span>
      )}
    </button>
  );
}
